import { IconButton, Tooltip } from '@ads/components-react';
import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { copyToClipboard } from '@/shared/utils/copyToClipboard';

export interface CopyableFieldProps {
  readonly label: string;
  /** Valor completo. É este que vai para a área de transferência. */
  readonly value: string;
}

/** Quanto tempo o retorno de "copiado" fica visível. */
const FEEDBACK_MS = 1500;

/**
 * Par rótulo-valor com controle de copiar, usado pela linha digitável e pelo
 * ticket de um boleto.
 *
 * O valor é truncado em **uma linha** na exibição — o `ticket` tem centenas de
 * caracteres e, despejado inline como na tela legada, empurra todo o resto da
 * tabela para fora da vista. O que é copiado é sempre o valor completo, nunca a
 * versão truncada.
 *
 * O retorno da cópia é um tooltip que abre no próprio botão e desaparece sozinho,
 * não um alerta global: copiar uma linha digitável é uma ação corriqueira e de
 * escopo local, e um alerta de página para cada cópia seria desproporcional —
 * além de empilhar avisos quando o operador copia vários boletos em sequência.
 */
export function CopyableField({ label, value }: CopyableFieldProps) {
  const { t } = useTranslation('individualPayment');
  const [feedback, setFeedback] = useState<'copied' | 'failed' | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Recolher a linha da parcela desmonta este componente, então o timer precisa
  // ser limpo — senão o `setFeedback` cai em um componente já desmontado.
  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const handleCopy = async () => {
    const copied = await copyToClipboard(value);
    setFeedback(copied ? 'copied' : 'failed');
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setFeedback(null), FEEDBACK_MS);
  };

  const tooltipTitle =
    feedback === 'copied'
      ? t('parcels.serials.copied')
      : feedback === 'failed'
        ? t('parcels.serials.copyFailed')
        : t('parcels.serials.copy', { field: label });

  return (
    <div className="flex items-center gap-micro">
      <span className="w-[7rem] shrink-0 ts-subtitle-xxs text-ctx-content-placeholder">
        {label}
      </span>
      {/* `min-w-none` + `truncate` no filho: sem o mínimo zerado o flex se recusa
          a encolher o texto e o botão de copiar é empurrado para fora. */}
      <span className="min-w-none grow overflow-hidden">
        <span className="block truncate ts-caption-xxs tabular-nums text-ctx-content-base">
          {value}
        </span>
      </span>
      {/* Sem `open`, o tooltip segue no comportamento normal de hover; só a cópia
          o força aberto, para o retorno aparecer sem depender do mouse. A chave
          precisa estar *ausente* (e não `undefined`) para o Radix voltar ao modo
          não-controlado — e `exactOptionalPropertyTypes` também exige isso. */}
      <Tooltip title={tooltipTitle} rootProps={feedback !== null ? { open: true } : {}}>
        <IconButton
          icon={feedback === 'copied' ? CheckIcon : ClipboardDocumentIcon}
          hierarchy="secondary"
          size="sm"
          ariaLabel={t('parcels.serials.copy', { field: label })}
          onClick={() => void handleCopy()}
        />
      </Tooltip>
    </div>
  );
}
